import { useEffect, useRef, useState } from "react";
import type { Candle } from "../types";
import { generateDemoCandles, TIMEFRAME_SECONDS, type Timeframe, fetchRealCandles } from "../lib/feed";

const HISTORY_BARS = 1500;

interface MarketData {
  candles: Candle[];
  live: boolean;
  toggleLive: () => void;
  timeframe: Timeframe;
  setTimeframe: (tf: Timeframe) => void;
}

/**
 * Stage 1 market data source — demo XAUUSD random-walk generator with
 * simulated live ticks. Replace `generateDemoCandles` / tick interval
 * with a Finnhub or TwelveData WebSocket (see lib/feed.ts MarketFeed).
 */
export function useMarketData(): MarketData {
  const [timeframe, setTimeframe] = useState<Timeframe>("M1");
  const [candles, setCandles] = useState<Candle[]>([]);
  const [live, setLive] = useState(true);
  const liveRef = useRef(live);
  liveRef.current = live;

  useEffect(() => {
    let mounted = true;
    (async () => {
      const real = await fetchRealCandles(HISTORY_BARS);
      if (mounted && real.length > 0) {
        setCandles(real);
        setLive(true);
      } else if (mounted) {
        setCandles(generateDemoCandles(HISTORY_BARS, timeframe));
      }
    })();
    return () => { mounted = false; };
  }, []);


  // Regenerate history when timeframe changes
  useEffect(() => {
    setCandles(generateDemoCandles(HISTORY_BARS, timeframe));
  }, [timeframe]);

  // Simulated live feed: append one closed candle per interval
  useEffect(() => {
    const step = TIMEFRAME_SECONDS[timeframe];
    const intervalMs = timeframe === "M1" ? 5000 : 8000;

    const id = window.setInterval(() => {
      if (!liveRef.current) return;

      setCandles((prev) => {
        const last = prev[prev.length - 1];
        const rand = Math.random() - 0.5;
        const open = last.close;
        const close = Math.round((open * (1 + rand * 0.0006)) * 100) / 100;
        const wick = Math.abs(close - open) + Math.random() * 0.45;
        const next: Candle = {
          time: last.time + step,
          open,
          high: Math.round((Math.max(open, close) + wick * Math.random()) * 100) / 100,
          low: Math.round((Math.min(open, close) - wick * Math.random()) * 100) / 100,
          close,
          volume: Math.round(80 + Math.random() * 620),
        };
        return [...prev.slice(1), next];
      });
    }, intervalMs);

    return () => window.clearInterval(id);
  }, [timeframe]);

  return {
    candles,
    live,
    toggleLive: () => setLive((v) => !v),
    timeframe,
    setTimeframe,
  };
}
